import type { IntentQuestion, IntentQuestionOption } from "./companyIntent.types";
import {
  hasAnswerGroups,
  INTENT_QUESTION_OPTION_COUNT,
} from "./intentQuestionnaireDimensions";

export function flattenAnswerGroupOptions(question: IntentQuestion): IntentQuestionOption[] {
  const seen = new Set<string>();
  const flattened: IntentQuestionOption[] = [];
  for (const group of question.answerGroups ?? []) {
    if (group.inputType === "catalog_geography") continue;
    for (const option of group.options ?? []) {
      const id = option?.id?.trim();
      const label = option?.label?.trim();
      if (!id || !label || seen.has(id)) continue;
      seen.add(id);
      flattened.push({
        id,
        label,
        tags: Array.isArray(option.tags) ? option.tags.filter(Boolean) : [],
      });
    }
  }
  return flattened;
}

export function displayOptionsForIntentQuestion(question: IntentQuestion): IntentQuestionOption[] {
  const topLevel = (question.options ?? []).filter((option) => option?.id && option?.label);
  if (topLevel.length > 0) return topLevel;
  return flattenAnswerGroupOptions(question);
}

export function padIntentOptions(
  options: IntentQuestionOption[],
  prefix: string,
  count: number = INTENT_QUESTION_OPTION_COUNT,
): IntentQuestionOption[] {
  const result = [...options];
  while (result.length < count) {
    const idx = result.length + 1;
    result.push({
      id: `${prefix}_pad_${idx}`,
      label: `Option ${idx}`,
      tags: [`${prefix}_option_${idx}`],
    });
  }
  return result.slice(0, count);
}

/** Flatten nested group choices onto options and drop leftover groups on non-Q15 questions. */
export function hydrateIntentQuestionForDisplay(question: IntentQuestion): IntentQuestion {
  const options = displayOptionsForIntentQuestion(question);
  if (hasAnswerGroups(question.id)) {
    return { ...question, options };
  }
  const { answerGroups: _answerGroups, ...rest } = question;
  return { ...rest, options };
}

export function hydrateIntentQuestionsForDisplay(questions: IntentQuestion[]): IntentQuestion[] {
  return questions.map(hydrateIntentQuestionForDisplay);
}
